import * as React from 'react';
import Flex from 'ui/Flex/Flex';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useDebounce } from 'hooks/helpers';
import { useAlertForm } from '../Contexts/AlertFormContext';
import PreviousStepButton from './PreviousStepButton';
import NextStepButton from './NextStepButton';
import { useAlertAction } from '../Contexts/AlertActionContext';

type AlertPersonalizationErrorKey = 'name' | 'emailSubject';

const AlertPersonalization = () => {
    const { alert, setAlert } = useAlertForm();
    const { action } = useAlertAction();
    const { t } = useTranslation();

    const [name, setName] = useState(alert.name ?? '');
    const [emailSubject, setEmailSubject] = useState(alert.emailSubject ?? '');
    const [emailContent, setEmailContent] = useState(alert.emailContent ?? '');

    const [errors, setErrors] = useState<
        Record<AlertPersonalizationErrorKey, boolean>
    >({
        name: false,
        emailSubject: false,
    });

    const updateName = useDebounce((newName: string) => {
        setAlert((prevState) => ({
            ...prevState,
            name: newName.trim(),
        }));
    }, 500);

    const updateSubject = useDebounce((newSubject: string) => {
        setAlert((prevState) => ({
            ...prevState,
            emailSubject: newSubject,
        }));
    }, 500);

    const updateContent = useDebounce((newContent: string) => {
        setAlert((prevState) => ({
            ...prevState,
            emailContent: newContent,
        }));
    }, 500);

    const onNameChange = (value: string): void => {
        setName(value);
        setErrors((prevState) => ({
            ...prevState,
            name: !value.trim(),
        }));
        updateName(value);
    };

    const onEmailSubjectChange = (value: string): void => {
        setEmailSubject(value);
        setErrors((prevState) => ({
            ...prevState,
            emailSubject: !value,
        }));
        updateSubject(value);
    };

    const onEmailContentChange = (value: string): void => {
        setEmailContent(value);
        updateContent(value);
    };

    const isDisabled = !(name?.trim() && emailSubject);

    return (
        <Flex direction="column" sx={{ mt: 2 }}>
            <TextField
                sx={{ mb: 2 }}
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                size="small"
                label={t('alert.name')}
                required
                error={errors.name}
            />
            <TextField
                sx={{ mb: 2 }}
                value={emailSubject}
                onChange={(e) => onEmailSubjectChange(e.target.value)}
                size="small"
                label={t('alert.emailSubject')}
                required
                error={errors.emailSubject}
            />
            <TextField
                value={emailContent}
                onChange={(e) => onEmailContentChange(e.target.value)}
                label={t('alert.emailContent')}
                multiline
                rows={10}
            />
            {action.name === 'create' && (
                <Flex align="center" sx={{ gap: 2 }}>
                    <PreviousStepButton />
                    <NextStepButton
                        disabled={isDisabled}
                        className="pendo-alerts-perso-step"
                    />
                </Flex>
            )}
        </Flex>
    );
};

export default AlertPersonalization;
